import { MedicaoAnteriorModel } from '@atendimento/detail/components/modals/medicoes/model-medicoes'
import { Cell, Grid, Theme, VFlow } from 'bold-ui'
import { useScrollPosition } from 'bold-ui/lib/hooks'
import CheckPermission from 'components/auth/CheckPermission'
import { useFlags } from 'config/useFlagsContext'
import { AlertaSuporteDecisaoClinica } from 'hooks/sdc/model-sdc'
import { useCabecalhoFixo } from 'hooks/useCabecalhoFixo'
import { CSSProperties, forwardRef, useMemo } from 'react'
import { isCidadaoIdoso } from 'util/isCidadaoIdoso'
import { useLocalStyles } from 'view/atendimentos/atendimento-individual/AtendimentoIndividualView'
import { CiapCidPreNatal, meta } from 'view/atendimentos/atendimento-individual/model'
import { AntropometriaAccordionModel } from 'view/atendimentos/components/MedicoesPanel/AntropometriaAccordion'
import { SinaisVitaisAccordionModel } from 'view/atendimentos/components/MedicoesPanel/SinaisVitaisAccordion'
import { LotacaoAtendimento } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { grupoCboVisualizarAcompanhamentos } from 'view/cidadao/authorization/acessos'

import { LembreteFormModel } from '../../components/modals/lembretes/components/LembreteForm'
import { grupoCboListaProblemasCondicoes } from '../../components/modals/lista-problemas/acessos'
import { PeriodoGestacaoModel } from '../../components/modals/types/PeriodoGestacaoModel'
import { grupoCboAlergias } from '../avaliacao/acessos'
import { AlergiaReacaoModel } from '../avaliacao/components/alergias-reacoes/model'
import { ProblemaCondicaoModel } from '../avaliacao/components/problemas-condicoes/model-problemasCondicoes'
import { grupoCboMedicoes, grupoCboResultadosExames } from '../objetivo/acessos'
import { IvcfFieldModel } from '../objetivo/ivcf/model-ivcf'
import { PuericulturaModel } from '../objetivo/puericultura/model'
import { ResultadosExamesModel } from '../objetivo/resultados-exames/model-resultadosExames'
import { grupoCboLembretes } from '../plano/acessos'
import { grupoCboAcompanhamentoPreNatal } from '../pre-natal/acessos'
import { PreNatalFormModel } from '../pre-natal/model-preNatal'
import { grupoCboAcompanhamentoIdoso } from './acompanhamento-idoso/acessos'
import { AcompanhamentoIdosoSection } from './acompanhamento-idoso/AcompanhamentoIdosoSection'
import { AcompanhamentoPuericulturaSection } from './acompanhamento-puericultura/AcompanhamentoPuericulturaSection'
import { AcompanhamentoVulnerabilidadeSection } from './acompanhamento-vulnerabilidade/AcompanhamentoVulnerabilidadeSection'
import { AlergiasSection } from './alergias/AlergiasSection'
import { CondicoesAutorreferidasSection } from './condicoes-autorreferidas/CondicoesAutorreferidasSection'
import { LembretesSection } from './lembretes/LembretesSection'
import { ProblemasSection } from './lista-problemas/ProblemasSection'
import { MedicamentosSection } from './medicamentos/MedicamentosSection'
import { MedicoesSection } from './medicoes/MedicoesSection'
import { AsideViewAtendimentoStatuses } from './model-aside'
import { PreNatalSection } from './pre-natal/components/PreNatalSection'
import { IdadeGestacionalAcompanhamentoPreNatal } from './pre-natal/model-acompanhamentoPreNatal'
import { PuerperioSection } from './puerperio/PuerperioSection'
import { RastreamentosSection } from './rastreamentos/components/RastreamentosSection'
import { ResultadosExamesSection } from './resultados-exames/ResultadosExamesSection'

interface AsideViewProps {
  lotacao?: LotacaoAtendimento
  prontuarioId: ID
  atendimentoProfissionalId?: ID
  atendimentoId?: ID
  dataAtendimento: Instant
  cidadao: CidadaoAtendimento
  isGestante: boolean
  isPuerpera?: boolean
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  ciapCidPreNatal?: CiapCidPreNatal
  problemasAvaliacao?: ProblemaCondicaoModel[]
  problemasLPC?: ReadonlyArray<ProblemaCondicaoModel>
  alergiasAtendimentoAtual?: ReadonlyArray<AlergiaReacaoModel>
  sinaisVitaisAtual?: SinaisVitaisAccordionModel
  antropometriaAtual?: AntropometriaAccordionModel
  lembretesCache?: ReadonlyArray<LembreteFormModel>
  vacinacaoEmDia?: boolean
  puericultura?: PuericulturaModel
  preNatal?: PreNatalFormModel
  dum?: string
  resultadosExames?: ResultadosExamesModel
  readOnlyMedicoesSection?: boolean
  isGravidezAltoRisco?: boolean
  width: number
  headerRef: React.RefObject<HTMLDivElement>
  idadeGestacional?: IdadeGestacionalAcompanhamentoPreNatal
  hasPermissionPreNatal?: boolean
  atendimentoStatuses: AsideViewAtendimentoStatuses
  readOnly?: boolean
  isRegistroTardio?: boolean
  ivcfAtendimentoAtual: IvcfFieldModel
  alertasSdc?: AlertaSuporteDecisaoClinica[]
  medicoesAnterioresAtendimentoAtual?: MedicaoAnteriorModel[]
  acompanhamentosBasePath?: string
}

export const AsideView = forwardRef<HTMLDivElement, AsideViewProps>((props, ref) => {
  const {
    prontuarioId,
    atendimentoId,
    atendimentoProfissionalId,
    dataAtendimento,
    cidadao,
    isGestante,
    isPuerpera,
    gestacoes,
    problemasAvaliacao,
    problemasLPC,
    alergiasAtendimentoAtual,
    sinaisVitaisAtual,
    antropometriaAtual,
    lembretesCache,
    vacinacaoEmDia,
    puericultura,
    preNatal,
    dum,
    resultadosExames,
    readOnlyMedicoesSection,
    isGravidezAltoRisco,
    ciapCidPreNatal,
    lotacao,
    width,
    headerRef,
    idadeGestacional,
    hasPermissionPreNatal = false,
    atendimentoStatuses,
    readOnly = false,
    isRegistroTardio = false,
    ivcfAtendimentoAtual,
    alertasSdc,
    medicoesAnterioresAtendimentoAtual,
    acompanhamentosBasePath,
  } = props

  const { PILOTO_RASTREAMENTO_ENABLED } = useFlags()

  const headerHeight: number = headerRef.current?.clientHeight

  const { scrollY } = useScrollPosition()
  const isCabecalhoFixo = useCabecalhoFixo()
  const { classes } = useLocalStyles(createStyles, isCabecalhoFixo, scrollY, headerHeight, width)

  const accessAcompanhamentoPuericultura = cidadao.idadeEmAnos < 19

  const {
    isAtendimentoObservacao,
    isRetificacao,
    isAtendimentoProcedimentos,
    isAtendimentoOdonto,
  } = atendimentoStatuses
  const isRetificacaoAtendimentoProcedimentos = isRetificacao && isAtendimentoProcedimentos
  const showRastreamentosSection = PILOTO_RASTREAMENTO_ENABLED && !readOnly

  const renderSections = useMemo(() => {
    return (
      <div ref={ref}>
        <VFlow>
          <Grid>
            <Cell size={12}>
              {!isRetificacao && (
                <>
                  {accessAcompanhamentoPuericultura && (
                    <AcompanhamentoPuericulturaSection
                      prontuarioId={prontuarioId}
                      vacinacaoEmDia={vacinacaoEmDia}
                      puericultura={puericultura}
                      cidadao={cidadao}
                      dataReferencia={dataAtendimento}
                      atendimentoId={atendimentoId}
                    />
                  )}
                  {isCidadaoIdoso(cidadao.idadeEmAnos) && (
                    <CheckPermission permission={grupoCboAcompanhamentoIdoso}>
                      <AcompanhamentoIdosoSection
                        prontuarioId={prontuarioId}
                        cidadaoId={cidadao.id}
                        ivcfAtendimentoAtual={ivcfAtendimentoAtual}
                      />
                    </CheckPermission>
                  )}
                  {isPuerpera && (
                    <CheckPermission permission={grupoCboVisualizarAcompanhamentos}>
                      <PuerperioSection
                        prontuarioId={prontuarioId}
                        cidadaoId={cidadao.id}
                        atendimentoId={atendimentoId}
                        dataReferencia={dataAtendimento}
                        acompanhamentosBasePath={acompanhamentosBasePath}
                      />
                    </CheckPermission>
                  )}
                  {isGestante && (
                    <CheckPermission permission={grupoCboAcompanhamentoPreNatal}>
                      <PreNatalSection
                        prontuarioId={prontuarioId}
                        cidadaoId={cidadao.id}
                        isAltoRiscoAtendimentoAtual={isGravidezAltoRisco}
                        preNatalAtendimentoAtual={preNatal}
                        dumAtendimentoAtual={dum}
                        resultadosExamesAtendimentoAtual={resultadosExames}
                        dataAtendimento={dataAtendimento}
                        atendimentoId={atendimentoId}
                        acompanhamentosBasePath={acompanhamentosBasePath}
                        readOnly={readOnly || !hasPermissionPreNatal}
                        idadeGestacional={idadeGestacional}
                      />
                    </CheckPermission>
                  )}
                </>
              )}
              {!isRetificacaoAtendimentoProcedimentos && (
                <>
                  <CheckPermission permission={grupoCboAlergias.visualizar}>
                    <AlergiasSection
                      prontuarioId={prontuarioId}
                      alergiasAtendimentoAtual={alergiasAtendimentoAtual ?? []}
                      fieldName={meta.avaliacao.alergias}
                    />
                  </CheckPermission>
                  <CheckPermission permission={grupoCboListaProblemasCondicoes.visualizar}>
                    <ProblemasSection
                      cidadao={cidadao}
                      ciapCidPreNatal={ciapCidPreNatal}
                      problemasAvaliacao={problemasAvaliacao}
                      problemasLPC={problemasLPC}
                      dataReferencia={dataAtendimento}
                      prontuarioId={prontuarioId}
                      isAtendimentoObservacao={isAtendimentoObservacao}
                      readOnly={readOnly || atendimentoStatuses.isAtendimentoProcedimentos}
                    />
                  </CheckPermission>
                </>
              )}
              {!isRetificacao && (
                <>
                  {showRastreamentosSection && (
                    <RastreamentosSection alertasSdc={alertasSdc} prontuarioId={prontuarioId} />
                  )}
                  <AcompanhamentoVulnerabilidadeSection cidadaoId={cidadao.id} dataReferencia={dataAtendimento} />
                </>
              )}
              <CheckPermission permission={grupoCboMedicoes.visualizar}>
                <MedicoesSection
                  prontuarioId={prontuarioId}
                  fieldName={meta.medicoesAnteriores}
                  sinaisVitaisAtual={sinaisVitaisAtual}
                  antropometriaAtual={antropometriaAtual}
                  cidadao={{
                    id: cidadao.id,
                    dataNascimento: cidadao.dataNascimento,
                    sexo: cidadao.sexo,
                    identidadeGeneroDbEnum: cidadao?.identidadeGeneroDbEnum,
                  }}
                  isGestante={isGestante}
                  gestacoes={gestacoes}
                  preNatalAtendimentoAtual={preNatal}
                  dumAtendimentoAtual={dum}
                  resultadosExamesAtendimentoAtual={resultadosExames}
                  dataAtendimento={dataAtendimento}
                  readOnly={readOnly || readOnlyMedicoesSection}
                  isAtendimentoObservacao={isAtendimentoObservacao}
                  tipoPreNatal={atendimentoStatuses.tipoPreNatal}
                  isRetificacao={isRetificacao}
                  isAtendimentoOdonto={isAtendimentoOdonto}
                  isAtendimentoProcedimentos={isAtendimentoProcedimentos}
                  medicoesAnterioresAtendimentoAtual={medicoesAnterioresAtendimentoAtual}
                />
              </CheckPermission>
              {!isRetificacaoAtendimentoProcedimentos && (
                <MedicamentosSection
                  editable={!readOnly && !atendimentoStatuses.isObservacaoAndAuxiliar}
                  prontuarioId={prontuarioId}
                  dataReferencia={dataAtendimento}
                  isCidadaoIdoso={isCidadaoIdoso(cidadao.idadeEmAnos)}
                  atendimentoProfissionalId={atendimentoProfissionalId}
                  atendimentoId={atendimentoId}
                  isAtendimentoObservacao={isAtendimentoObservacao}
                  isRegistroTardio={isRegistroTardio}
                />
              )}
              {!isRetificacao && (
                <>
                  <CondicoesAutorreferidasSection cidadaoId={cidadao.id} />
                  <CheckPermission permission={grupoCboLembretes.visualizar}>
                    <LembretesSection
                      lotacao={lotacao}
                      prontuarioId={prontuarioId}
                      lembretesCache={lembretesCache}
                      meta={meta.lembretes}
                      isEditable={!readOnly}
                      isAtendimentoObservacao={isAtendimentoObservacao}
                    />
                  </CheckPermission>
                  <CheckPermission permission={grupoCboResultadosExames.visualizar}>
                    <ResultadosExamesSection
                      prontuarioId={prontuarioId}
                      atendimentoProfissionalId={atendimentoProfissionalId}
                      isAtendimentoObservacao={isAtendimentoObservacao}
                    />
                  </CheckPermission>
                </>
              )}
            </Cell>
          </Grid>
        </VFlow>
      </div>
    )
  }, [
    ref,
    isRetificacao,
    accessAcompanhamentoPuericultura,
    prontuarioId,
    vacinacaoEmDia,
    puericultura,
    cidadao,
    dataAtendimento,
    atendimentoId,
    acompanhamentosBasePath,
    ivcfAtendimentoAtual,
    isGestante,
    isPuerpera,
    isGravidezAltoRisco,
    preNatal,
    dum,
    resultadosExames,
    readOnly,
    hasPermissionPreNatal,
    idadeGestacional,
    isRetificacaoAtendimentoProcedimentos,
    alergiasAtendimentoAtual,
    ciapCidPreNatal,
    problemasAvaliacao,
    problemasLPC,
    isAtendimentoObservacao,
    atendimentoStatuses.isAtendimentoProcedimentos,
    atendimentoStatuses.tipoPreNatal,
    atendimentoStatuses.isObservacaoAndAuxiliar,
    antropometriaAtual,
    gestacoes,
    readOnlyMedicoesSection,
    isAtendimentoOdonto,
    isAtendimentoProcedimentos,
    atendimentoProfissionalId,
    isRegistroTardio,
    lotacao,
    lembretesCache,
    showRastreamentosSection,
    alertasSdc,
    sinaisVitaisAtual,
    medicoesAnterioresAtendimentoAtual,
  ])

  return <aside className={classes.aside}>{renderSections}</aside>
})

const createStyles = (
  theme: Theme,
  isCabecalhoFixo: boolean,
  scrollY: number,
  headerHeight: number,
  width: number
) => ({
  aside: {
    width: width ?? '100%',
    paddingRight: '1rem',
    position: isCabecalhoFixo ? 'fixed' : 'static',
    top: isCabecalhoFixo && headerHeight,
    marginTop: isCabecalhoFixo && '1rem',
    height: `calc(100% - ${isCabecalhoFixo ? calcAsideHeightDiff(scrollY) + 'rem' : '0px'})`,
    overflow: isCabecalhoFixo ? 'hidden auto' : 'hidden',
    '::-webkit-scrollbar': {
      width: '0.3rem',
      height: '0.3rem',
    },
    '::-webkit-scrollbar-thumb': {
      background: theme.pallete.gray.c40,
      borderRadius: theme.radius.tag,
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: theme.pallete.gray.c60,
    },
    '::-webkit-scrollbar-track': {
      background: theme.pallete.surface.main,
      borderRadius: theme.radius.tag,
      boxShadow: `inset 0.4rem 0.625rem 0.75rem ${theme.pallete.divider}`,
    },
  } as CSSProperties,
})

function calcAsideHeightDiff(scrollY: number): number {
  const distBottom = window.document.body.offsetHeight - (scrollY + window.innerHeight)
  return Math.max(0, 6.1 - distBottom / 16) + 9
}
